import { ExperimentOutlined, ReloadOutlined, RobotOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Divider, Empty, List, Spin, Tag, Tooltip, Typography } from "antd";
import ReactMarkdown from "react-markdown";

const { Title, Text, Paragraph } = Typography;

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 13, color: "#1a3e5c", textTransform: "uppercase", letterSpacing: 0.4 }}>
            {title}
        </Text>
        <div style={{ marginTop: 6, fontSize: 13, color: "#333", lineHeight: 1.6 }}>{children}</div>
    </div>
);

const Markdown = ({ text }) =>
    text ? <ReactMarkdown>{String(text)}</ReactMarkdown> : <Text type="secondary">Not provided.</Text>;

const StringList = ({ items, emptyText }) => {
    if (!Array.isArray(items) || items.length === 0) {
        return <Text type="secondary">{emptyText || "None provided."}</Text>;
    }
    return (
        <List
            size="small"
            dataSource={items}
            split={false}
            renderItem={(item) => (
                <List.Item style={{ padding: "2px 0", border: "none" }}>
                    <span style={{ fontSize: 13 }}>• {item}</span>
                </List.Item>
            )}
        />
    );
};

const DataQualityAssessment = ({ dq }) => {
    if (!dq) return <Text type="secondary">Not provided.</Text>;
    return (
        <div>
            {dq.overall && (
                <div style={{ marginBottom: 8 }}>
                    <Markdown text={dq.overall} />
                </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
                {dq.completeness && (
                    <div style={{ fontSize: 13 }}>
                        <Text strong>Completeness: </Text>
                        {dq.completeness}
                    </div>
                )}
                {dq.uniqueness && (
                    <div style={{ fontSize: 13 }}>
                        <Text strong>Uniqueness: </Text>
                        {dq.uniqueness}
                    </div>
                )}
                {dq.validity && (
                    <div style={{ fontSize: 13 }}>
                        <Text strong>Validity: </Text>
                        {dq.validity}
                    </div>
                )}
            </div>
            {Array.isArray(dq.issues) && dq.issues.length > 0 && (
                <>
                    <Text strong style={{ fontSize: 12, color: "#fa541d" }}>
                        Issues
                    </Text>
                    <StringList items={dq.issues} emptyText="No issues detected." />
                </>
            )}
        </div>
    );
};

const VisualizationSuggestions = ({ items }) => {
    if (!Array.isArray(items) || items.length === 0) {
        return <Text type="secondary">None provided.</Text>;
    }
    return (
        <List
            size="small"
            dataSource={items}
            split={false}
            renderItem={(item) => (
                <List.Item style={{ padding: "4px 0", border: "none", display: "block" }}>
                    <div style={{ fontSize: 13 }}>
                        {item.chart && (
                            <Tag color="geekblue" style={{ marginRight: 6 }}>
                                {item.chart}
                            </Tag>
                        )}
                        {Array.isArray(item.fields) && item.fields.length > 0 && (
                            <Text code style={{ fontSize: 11 }}>
                                {item.fields.join(", ")}
                            </Text>
                        )}
                        {item.rationale && <span style={{ color: "#555" }}> — {item.rationale}</span>}
                    </div>
                </List.Item>
            )}
        />
    );
};

const DescriptionMeta = ({ data }) => {
    const desc = data.description || {};
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <RobotOutlined style={{ color: "#722ed1" }} />
            <Text strong style={{ fontSize: 14 }}>
                {data.dataSetName || "Dataset"}
            </Text>
            {data.recordSetVersion != null && (
                <Tag color="blue" style={{ margin: 0 }}>
                    v{data.recordSetVersion}
                </Tag>
            )}
            {data.totalRecords != null && (
                <Tag style={{ margin: 0 }}>{Number(data.totalRecords).toLocaleString()} records</Tag>
            )}
            {desc.fallback && (
                <Tooltip title="Generated from profiling statistics without the AI narrative service.">
                    <Tag color="orange" style={{ margin: 0 }}>
                        Statistics-based
                    </Tag>
                </Tooltip>
            )}
            {data.generatedAt && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: "auto" }}>
                    Generated {new Date(data.generatedAt).toLocaleString()}
                </Text>
            )}
        </div>
    );
};

const DatasetDescription = ({
    datasetDescription,
    loading,
    result,
    profilingAvailable,
    canGenerate,
    onGenerate,
    onRegenerate,
}) => {
    if (loading) {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
                <Spin size="large" />
                <Text type="secondary">Generating dataset description… this can take up to a minute.</Text>
            </div>
        );
    }

    const hasError = result && result.success === false;

    if (hasError) {
        return (
            <div style={{ padding: 16 }}>
                <Alert
                    type="error"
                    showIcon
                    message="Could not generate dataset description"
                    description={result.message || "Something went wrong. Please try again."}
                    action={
                        canGenerate ? (
                            <Button size="small" icon={<ReloadOutlined />} onClick={() => onGenerate()}>
                                Retry
                            </Button>
                        ) : null
                    }
                />
            </div>
        );
    }

    if (!datasetDescription) {
        const disabledReason = !profilingAvailable
            ? "Run dataset profiling first to enable AI description."
            : !canGenerate
              ? "Dataset context is unavailable. Re-open the dataset and try again."
              : null;
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: 24 }}>
                <Empty
                    image={<RobotOutlined style={{ fontSize: 48, color: "#722ed1" }} />}
                    description={
                        <div style={{ maxWidth: 420, textAlign: "center" }}>
                            <Text strong>Describe this dataset with AI</Text>
                            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6 }}>
                                Generates an executive summary, data-quality assessment, use cases and recommendations
                                grounded strictly in the profiling statistics. PII field values are never sent to the model.
                            </Paragraph>
                        </div>
                    }
                />
                <Tooltip title={disabledReason || ""}>
                    <Button
                        type="primary"
                        icon={<ExperimentOutlined />}
                        disabled={!canGenerate}
                        onClick={() => onGenerate()}
                    >
                        Generate description
                    </Button>
                </Tooltip>
            </div>
        );
    }

    const desc = datasetDescription.description || {};
    const redacted = Array.isArray(datasetDescription.redactedPiiFields) ? datasetDescription.redactedPiiFields : [];

    return (
        <div style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
            <Card size="small" bodyStyle={{ padding: "12px 16px" }} style={{ borderColor: "#e8e8e8" }}>
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                        <DescriptionMeta data={datasetDescription} />
                    </div>
                    <Tooltip title="Regenerate a fresh description">
                        <Button size="small" icon={<ReloadOutlined />} onClick={() => onRegenerate()}>
                            Regenerate
                        </Button>
                    </Tooltip>
                </div>

                {datasetDescription.warningMessage && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={datasetDescription.warningMessage}
                    />
                )}

                {redacted.length > 0 && (
                    <Alert
                        type="info"
                        showIcon
                        icon={<SafetyCertificateOutlined />}
                        style={{ marginBottom: 12 }}
                        message={`PII protected: raw values for ${redacted.length} field(s) were withheld from the model (${redacted
                            .slice(0, 8)
                            .join(", ")}${redacted.length > 8 ? "…" : ""}).`}
                    />
                )}

                <Title level={5} style={{ marginTop: 4 }}>
                    Executive Summary
                </Title>
                <Markdown text={desc.executiveSummary} />

                <Divider style={{ margin: "12px 0" }} />

                <Section title="Detailed Description">
                    <Markdown text={desc.detailedDescription} />
                </Section>

                <Section title="Potential Use Cases">
                    <StringList items={desc.potentialUseCases} emptyText="No use cases suggested." />
                </Section>

                <Section title="Data Quality Assessment">
                    <DataQualityAssessment dq={desc.dataQualityAssessment} />
                </Section>

                <Section title="Visualization Suggestions">
                    <VisualizationSuggestions items={desc.visualizationSuggestions} />
                </Section>

                <Section title="Recommendations">
                    <StringList items={desc.recommendations} emptyText="No recommendations provided." />
                </Section>
            </Card>
        </div>
    );
};

export default DatasetDescription;
